import { useSession } from "@/services/auth0/session";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL;

// ========================= Types =========================

export type ShiftType = "MORNING" | "AFTERNOON" | "EVENING";
export type ShiftStatus = "SCHEDULED" | "CONFIRMED" | "COMPLETED" | "CANCELLED";
export type RequestStatus = "OPEN" | "CLOSED" | "CANCELLED";

export interface AvailabilityRequest {
  id: string;
  dispatchCenterId: string;
  depotId?: string | null;
  weekStart: string;
  message: string | null;
  status: RequestStatus;
  createdBy: string;
  createdAt: string;
  closedAt: string | null;
  closedBy: string | null;
  depot?: { id: string; name: string } | null;
  totalOperators: number;
  submittedCount: number;
  operators: {
    id: string;
    name: string;
    submitted: boolean;
  }[];
}

export interface WeeklyAvailability {
  mon: ShiftType[];
  tue: ShiftType[];
  wed: ShiftType[];
  thu: ShiftType[];
  fri: ShiftType[];
  sat: ShiftType[];
  sun: ShiftType[];
}

export interface AvailabilityGridItem {
  operatorId: string;
  name: string;
  availability: WeeklyAvailability | null;
  notes: string | null;
  submittedAt: string | null;
}

export interface OperatorShift {
  id: string;
  operatorId: string;
  dispatchCenterId: string;
  depotId?: string | null;
  date: string;
  shiftType: ShiftType;
  startTime: string | null;
  endTime: string | null;
  status: ShiftStatus;
  notes: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  operator?: {
    id: string;
    user: { firstName: string; lastName: string };
  };
  depot?: { id: string; name: string } | null;
}

export interface ShiftsGridItem {
  operatorId: string;
  name: string;
  shifts: OperatorShift[];
}

// ========================= API Hooks =========================

// Get availability requests
export function useAvailabilityRequests(params?: {
  dispatchCenterId?: string;
  weekStart?: string;
  carrierId?: string;
}) {
  const { token } = useSession();

  return useQuery({
    queryKey: ["availability-requests", params],
    queryFn: async () => {
      const { data } = await axios.get<AvailabilityRequest[]>(
        `${API_URL}/api/v2/scheduling/requests`,
        {
          params,
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    enabled: !!token,
  });
}

// Create availability request
export function useCreateAvailabilityRequest(options?: {
  onSuccess?: (data: AvailabilityRequest) => void;
  onError?: (error: unknown) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      dispatchCenterId: string;
      weekStart: string;
      message?: string;
      operatorIds?: string[];
    }) => {
      const { data } = await axios.post<AvailabilityRequest>(
        `${API_URL}/api/v2/scheduling/requests`,
        input,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: ["availability-requests"],
      });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

// Get availability grid
export function useAvailabilityGrid(params: {
  weekStart: string;
  dispatchCenterId: string;
  carrierId?: string;
}) {
  const { token } = useSession();

  return useQuery({
    queryKey: ["availability-grid", params],
    queryFn: async () => {
      const { data } = await axios.get<AvailabilityGridItem[]>(
        `${API_URL}/api/v2/scheduling/availability`,
        {
          params,
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    enabled: !!token && !!params.weekStart && !!params.dispatchCenterId,
  });
}

// Get shifts grid
export function useShiftsGrid(params: {
  weekStart: string;
  dispatchCenterId: string;
  carrierId?: string;
}) {
  const { token } = useSession();

  return useQuery({
    queryKey: ["shifts-grid", params],
    queryFn: async () => {
      const { data } = await axios.get<ShiftsGridItem[]>(
        `${API_URL}/api/v2/scheduling/shifts`,
        {
          params,
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    enabled: !!token && !!params.weekStart && !!params.dispatchCenterId,
  });
}

// Create shift
export function useCreateShift(options?: {
  onSuccess?: (data: OperatorShift) => void;
  onError?: (error: unknown) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      operatorId: string;
      dispatchCenterId: string;
      date: string;
      shiftType: ShiftType;
      startTime?: string;
      endTime?: string;
      notes?: string;
    }) => {
      const { data } = await axios.post<OperatorShift>(
        `${API_URL}/api/v2/scheduling/shifts`,
        input,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["shifts-grid"] });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

// Update shift
export function useUpdateShift(options?: {
  onSuccess?: (data: OperatorShift) => void;
  onError?: (error: unknown) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      shiftId,
      ...input
    }: {
      shiftId: string;
      status?: ShiftStatus;
      startTime?: string;
      endTime?: string;
      notes?: string;
    }) => {
      const { data } = await axios.patch<OperatorShift>(
        `${API_URL}/api/v2/scheduling/shifts/${shiftId}`,
        input,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["shifts-grid"] });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

// Delete shift
export function useDeleteShift(options?: {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (shiftId: string) => {
      await axios.delete(`${API_URL}/api/v2/scheduling/shifts/${shiftId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["shifts-grid"] });
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
}

// Update availability request status (close/cancel/reopen)
export function useUpdateAvailabilityRequestStatus(options?: {
  onSuccess?: (data: AvailabilityRequest) => void;
  onError?: (error: unknown) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      requestId,
      status,
    }: {
      requestId: string;
      status: RequestStatus;
    }) => {
      const { data } = await axios.patch<AvailabilityRequest>(
        `${API_URL}/api/v2/scheduling/requests/${requestId}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: ["availability-requests"],
      });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

// Send reminder to operators who haven't submitted
export interface ReminderResponse {
  requestId: string;
  remindedCount: number;
  operators: { id: string; name: string; email: string }[];
}

export function useSendAvailabilityReminder(options?: {
  onSuccess?: (data: ReminderResponse) => void;
  onError?: (error: unknown) => void;
}) {
  const { token } = useSession();

  return useMutation({
    mutationFn: async (requestId: string) => {
      const { data } = await axios.post<ReminderResponse>(
        `${API_URL}/api/v2/scheduling/requests/${requestId}/remind`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });
}
